import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";

// Juventus-style sideways scrolling: grab a rail and drag it, with momentum, a grab cursor and
// a progress bar showing how much of the row is still to come.
export const attachDragScroll = (el) => {
  let down = false;
  let moved = 0;
  let startX = 0;
  let startLeft = 0;
  let lastX = 0;
  let velocity = 0;
  let frame = 0;

  const glide = () => {
    velocity *= 0.94;
    if (Math.abs(velocity) < 0.4) return;
    el.scrollLeft -= velocity;
    frame = requestAnimationFrame(glide);
  };

  const onDown = (e) => {
    if (e.button !== 0 || e.target.closest("input, select, textarea, button, [data-no-drag]")) return;
    down = true;
    moved = 0;
    velocity = 0;
    cancelAnimationFrame(frame);
    startX = lastX = e.clientX;
    startLeft = el.scrollLeft;
    el.dataset.dragging = "true";
    document.body.style.userSelect = "none";
    e.preventDefault();
  };
  const onMove = (e) => {
    if (!down) return;
    const dx = e.clientX - startX;
    moved = Math.max(moved, Math.abs(dx));
    velocity = e.clientX - lastX;
    lastX = e.clientX;
    if (moved > 4) {
      el.style.scrollSnapType = "none";
      el.scrollLeft = startLeft - dx;
    }
  };
  const onUp = () => {
    if (!down) return;
    down = false;
    delete el.dataset.dragging;
    document.body.style.userSelect = "";
    if (Math.abs(velocity) > 2) frame = requestAnimationFrame(glide);
  };
  const onClick = (e) => {
    if (moved > 6) {
      e.preventDefault();
      e.stopPropagation();
    }
    moved = 0;
  };
  const onCancel = () => {
    down = false;
    delete el.dataset.dragging;
    document.body.style.userSelect = "";
  };
  const onDragStart = (e) => e.preventDefault();

  el.addEventListener("pointerdown", onDown);
  el.addEventListener("dragstart", onDragStart);
  window.addEventListener("pointermove", onMove);
  window.addEventListener("pointerup", onUp);
  window.addEventListener("pointercancel", onCancel);
  el.addEventListener("click", onClick, true);

  return () => {
    cancelAnimationFrame(frame);
    el.removeEventListener("pointerdown", onDown);
    el.removeEventListener("dragstart", onDragStart);
    window.removeEventListener("pointermove", onMove);
    window.removeEventListener("pointerup", onUp);
    window.removeEventListener("pointercancel", onCancel);
    el.removeEventListener("click", onClick, true);
  };
};

export const useDragScroll = (ref) => {
  useEffect(() => {
    if (!ref.current) return;
    ref.current.dataset.dragRail = "1";
    return attachDragScroll(ref.current);
  }, [ref]);
};

export const useRailProgress = (ref) => {
  const [state, setState] = useState({ progress: 0, scrollable: false });
  const measure = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    const max = el.scrollWidth - el.clientWidth;
    setState({ progress: max > 4 ? Math.min(el.scrollLeft / max, 1) : 0, scrollable: max > 4 });
  }, [ref]);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    measure();
    el.addEventListener("scroll", measure, { passive: true });
    window.addEventListener("resize", measure);
    const obs = new ResizeObserver(measure);
    obs.observe(el);
    return () => {
      el.removeEventListener("scroll", measure);
      window.removeEventListener("resize", measure);
      obs.disconnect();
    };
  }, [ref, measure]);

  return state;
};

export const RailProgress = ({ progress, testId }) => (
  <div className="mt-4 h-[2px] w-full bg-white/10" data-testid={testId}>
    <div
      className="h-full bg-maasai transition-[width] duration-150"
      style={{ width: `${Math.max(progress * 100, 6)}%` }}
    />
  </div>
);

export const ScrollRail = ({
  children,
  testId = "rail",
  className = "",
  innerClassName = "",
  progressBar = true,
}) => {
  const ref = useRef(null);
  useDragScroll(ref);
  const { progress, scrollable } = useRailProgress(ref);

  return (
    <div className={className} data-testid={`${testId}-wrap`}>
      <div
        ref={ref}
        data-testid={testId}
        className={`no-scrollbar overflow-x-auto ${scrollable ? "cursor-grab active:cursor-grabbing" : ""} ${innerClassName}`}
      >
        {children}
      </div>
      {progressBar && scrollable ? <RailProgress progress={progress} testId={`${testId}-progress`} /> : null}
    </div>
  );
};

// Every horizontally scrollable block on the site — card rails, tab strips, wide data tables —
// becomes draggable, so the whole site scrolls sideways the same way.
export const GlobalDragRails = () => {
  const { pathname } = useLocation();

  useEffect(() => {
    const cleanups = [];
    const scan = () => {
      document.querySelectorAll("div, section").forEach((el) => {
        if (el.dataset.dragRail || el.scrollWidth - el.clientWidth < 8) return;
        const overflow = getComputedStyle(el).overflowX;
        if (overflow !== "auto" && overflow !== "scroll") return;
        el.dataset.dragRail = "1";
        el.classList.add("cursor-grab", "active:cursor-grabbing");
        cleanups.push(attachDragScroll(el));
      });
    };
    const timers = [setTimeout(scan, 250)];
    const interval = setInterval(scan, 900);
    const stop = setTimeout(() => clearInterval(interval), 20000);
    return () => {
      timers.forEach(clearTimeout);
      clearTimeout(stop);
      clearInterval(interval);
      cleanups.forEach((fn) => fn());
      document.querySelectorAll("[data-drag-rail]").forEach((el) => delete el.dataset.dragRail);
    };
  }, [pathname]);

  return null;
};
